import { useEffect, useState, useCallback, useRef } from "react";
import ValuerAmendFreeTextInput from "./ValuerAmendFreeTextInput";
import ValuerAmendExpandableTableContainer from "./ValuerAmendExpandableTableContainer";
import valuer_api from "../../api/valuer";
import plus from "../../assets/plus.svg";

const ValuerAmendCustomAdditionComponent = (props) => {
    const _summaryId = props.summaryId;
    const _sectionName = props.sectionName;
    const _identifier = props.identifier;
    const _rows = props.rows ?? 5;
    const _columns =
        props.columns ?? Array.from({ length: 11 }, (_, i) => ({ identifier: `col-${i + 1}`, default: "" }));

    // graceful‑fallback helpers
    const UpdateParentValue = props.UpdateParentValue ?? (() => {});
    const AddToQueue = props.AddToQueue ?? (() => {});
    const RemoveFromQueue = props.RemoveFromQueue ?? (() => {});
    const AddToErroredSaves = props.AddToErroredSaves ?? (() => {});

    const [_values, setValues] = useState({});
    const [components, setComponents] = useState([]);
    const editedTitleKeysRef = useRef(new Set());


    /** ------------------------------------------------------------------
     *  NEXT‑INDEX HELPERS
     * ------------------------------------------------------------------*/
    const getNextIndex = () => {
        const re = new RegExp(`${_identifier}-(?:textbox|table|title)-([0-9]+)$`);
        const nums = Object.keys(_values)
            .map((k) => {
                const m = k.match(re);
                return m ? parseInt(m[1], 10) : 0;
            })
            .filter(Boolean);                       // drop 0 / NaN
        return (nums.length ? Math.max(...nums) : 0) + 1;
    };

    const getNextTextboxKey = () => `${_identifier}-textbox-${getNextIndex()}`;

    const getNextTableKeys = () => {
        const idx = getNextIndex();
        return {
            tableKey: `${_identifier}-table-${idx}`,
            titleKey: `${_identifier}-title-${idx}`,
        };
    };


    /** ------------------------------------------------------------------
     *  BUILD COMPONENT LIST (textboxes + tables)
     * ------------------------------------------------------------------*/
    const buildComponentsFromValues = useCallback(
        (vals) => {
            const list = [];

            // textboxes
            Object.keys(vals)
                .filter((k) => k.startsWith(`${_identifier}-textbox-`))
                .forEach((key) =>
                    list.push({ id: `${key}-comp`, type: "textbox", identifier: key })
                );

            // tables & titles
            Object.entries(vals).forEach(([tableKey, obj]) => {
                if (!tableKey.startsWith(`${_identifier}-table-`) || typeof obj !== "object") return;

                const idx = tableKey.split("-").pop();
                const titleKey = `${_identifier}-title-${idx}`;

                // Always add the title textbox component if the table exists
                list.push({
                    id: `${tableKey}-${titleKey}-title`,
                    type: "table-title",
                    title_id: titleKey,
                    table_id: tableKey,
                });

                // Add the actual table
                list.push({
                    id: `${tableKey}-table`,
                    type: "table",
                    identifier: tableKey,
                });
            });

            // newest → oldest (4,3,2,1…)
            return list.sort((a, b) => {
                const getIdx = (c) =>
                    parseInt((c.identifier ?? c.table_id).split("-").pop(), 10);
                const diff = getIdx(b) - getIdx(a);
                if (diff) return diff;
                if (a.type === "table-title") return -1;
                if (b.type === "table-title") return 1;
                return 0;
            });
        },
        [_identifier]
    );


    /** ------------------------------------------------------------------
     *  EFFECT 1: merge incoming props.values (table/title logic unchanged)
     * ------------------------------------------------------------------*/
    useEffect(() => {
        if (!props.values) return;

        const prefix = `${_identifier}-`;
        const titlePrefix = `${_identifier}-title-`;

        Object.entries(props.values).forEach(([k, v]) => {
            if (k.startsWith(titlePrefix) && typeof v === "string" && v.trim() !== "") {
                editedTitleKeysRef.current.add(k);
            }
        });

        const incoming = Object.fromEntries(Object.entries(props.values).filter(([k]) => k.startsWith(prefix)));

        const patched = { ...incoming };
        Object.keys(incoming).forEach((k) => {
            if (!k.startsWith(`${_identifier}-table-`)) return;
            const idx = k.split("-").pop();
            const titleKey = `${_identifier}-title-${idx}`;
            if (incoming[titleKey] !== undefined) patched[k] = { ...patched[k], [titleKey]: incoming[titleKey] };
        });

        setValues((prev) => {
            const merged = { ...prev, ...patched };
            Object.keys(merged).forEach((k) => {
                if (!k.startsWith(`${_identifier}-table-`)) return;
                const idx = k.split("-").pop();
                const titleKey = `${_identifier}-title-${idx}`;
                if (merged[k][titleKey] === undefined) {
                    const fallback = merged[titleKey] ?? prev[k]?.[titleKey];
                    if (fallback !== undefined) merged[k] = { ...merged[k], [titleKey]: fallback };
                }
            });
            return merged;
        });
    }, [props.values, _identifier]);

    /** ------------------------------------------------------------------
     *  EFFECT 2: rebuild component list whenever state changes
     * ------------------------------------------------------------------*/
    useEffect(() => {
        setComponents(buildComponentsFromValues(_values));
    }, [_values, buildComponentsFromValues]);

    /** ------------------------------------------------------------------
     *  Backend commit helper
     * ------------------------------------------------------------------*/
    const commitValue = (section, field, value, rowOrder = null) => {
        const t = Date.now();
        AddToQueue(section, field, t);

        let payload = { value };


        if (field.includes("-title-")) {
            editedTitleKeysRef.current.add(field);
        }

        if (rowOrder !== null) {
            payload.order = rowOrder;
        } else if (field.includes("-addition-component-textbox-")) {
            const match = field.match(/-textbox-(\d+)$/);
            if (match) {
                payload.order = parseInt(match[1], 10);
            }
        }

        const isTableComponent =
            field.includes("-custom-addition-component-table-") ||
            field.includes("-custom-addition-component-title-");

        if (isTableComponent) {
            payload.updateNested = 1;
        }

        valuer_api
            .ValuerAmendPut(_summaryId, section, field, payload)
            .then(() => RemoveFromQueue(section, field, t))
            .catch(() => AddToErroredSaves(section, field, t));

        // ─────────────────────────────────────────────────────────────
        // Fallback: Ensure blank title exists and is flagged as nested
        // ─────────────────────────────────────────────────────────────
        if (
            isTableComponent &&
            !field.includes("-title-") &&
            field.includes("-table-")
        ) {
            const idxMatch = field.match(/-table-(\d+)$/);
            if (idxMatch) {
                const idx = idxMatch[1];
                const titleKey = `${_identifier}-title-${idx}`;
                const tableKey = `${_identifier}-table-${idx}`;

                const top = _values[titleKey];
                const nested = _values[tableKey]?.[titleKey];
                const incoming = props.values?.[titleKey];
                const present = (s) => typeof s === "string" && s.trim() !== "";
                const isTitleMissing = !present(top) && !present(nested) && !present(incoming);

                const isCellValueMeaningful = typeof value === "string" && value.trim() !== "";
                const alreadyEdited = editedTitleKeysRef.current.has(titleKey);

                if (isCellValueMeaningful && isTitleMissing && !alreadyEdited) {
                    valuer_api
                        .ValuerAmendPut(_summaryId, _sectionName, titleKey, {
                            value: "",
                            updateNested: 1,
                        })
                        .catch((err) => {
                            console.warn(`Failed to save fallback title '${titleKey}'`, err);
                            AddToErroredSaves(_sectionName, titleKey, Date.now());
                        });
                }
            }
        }
    };



    /** ------------------------------------------------------------------
     *  ADD COMPONENTS
     * ------------------------------------------------------------------*/
    const addTextbox = async () => {
        const key = getNextTextboxKey();
        const cid = Date.now();
        setValues((prev) => ({ ...prev, [key]: "" }));
        setComponents((prev) => [{ id: cid, type: "textbox", identifier: key }, ...prev]);
        try {
            await valuer_api.ValuerAmendPut(_summaryId, _sectionName, key, { value: "" });
        } catch (e) {
            console.error("Failed to create textbox field", e);
        }
    };

    const addTable = async () => {
        const { tableKey, titleKey } = getNextTableKeys();
        const rows = Object.fromEntries(
            Array.from({ length: _rows }, (_, i) => [
                String(i),
                { order: i + 1, ...Object.fromEntries(_columns.map((c) => [c.identifier, c.default ?? ""])) },
            ])
        );
        rows.__fresh = true; // Add flag to indicate this is a new insert

        rows[titleKey] = "";
        setValues((prev) => ({ ...prev, [tableKey]: rows, [titleKey]: "" }));
        try {
            await valuer_api.ValuerAmendPut(_summaryId, _sectionName, tableKey, { updateNested: 1, value: "NULL" });
        } catch (e) {
            console.error("Failed creating table", e);
        }
    };

    /** ------------------------------------------------------------------
     *  REMOVE COMPONENT (no backend delete yet)
     * ------------------------------------------------------------------*/
    const removeComponent = async (id) => {
        const target = components.find((c) => c.id === id);
        if (!target) return;

        // Determine if we are deleting a table (with title) or a textbox
        let identifierToDelete = null;

        if (target.type === "textbox") {
            identifierToDelete = target.identifier;
        } else if (target.type === "table") {
            identifierToDelete = target.identifier;
        } else if (target.type === "table-title") {
            // Don't allow deleting title alone – skip
            return;
        }

        // Optimistic UI update
        setComponents((prev) =>
            prev.filter((c) => {
                if (target.type === "textbox") return c.id !== id;
                if (target.type === "table") {
                    // remove both table and its title
                    return c.identifier !== target.identifier && c.table_id !== target.identifier;
                }
                return true;
            })
        );

        setValues((prev) => {
            const updated = { ...prev };
            if (target.type === "textbox") {
                delete updated[identifierToDelete];
            } else if (target.type === "table") {
                delete updated[identifierToDelete];
                // Also remove the top-level title field, if any
                const idx = identifierToDelete.split("-").pop();
                const titleKey = `${_identifier}-title-${idx}`;
                delete updated[titleKey];
            }
            return updated;
        });

        try {
            await valuer_api.ValuerAmendPut(_summaryId, _sectionName, identifierToDelete, { value: null });
        } catch (error) {
            console.error("Failed to delete component", error);
            AddToErroredSaves(_sectionName, identifierToDelete, Date.now());
        }
    };

    /** ------------------------------------------------------------------
     *  RENDERERS
     * ------------------------------------------------------------------*/
    const renderComponent = (comp) => {
        if (comp.type === "textbox") {
            const val = _values[comp.identifier] ?? "";
            return (
                <div className={"custom-component-textbox"} key={comp.id} >
                    <ValuerAmendFreeTextInput
                        sectionName={_sectionName}
                        identifier={comp.identifier}
                        textLimit={1024}
                        value={val}
                        field="Custom textbox"
                        placeholder="Enter text…"
                        summaryId={_summaryId}
                        UpdateParentValue={(id, v) => commitValue(_sectionName, id, v)}
                        AddToQueue={AddToQueue}
                        RemoveFromQueue={RemoveFromQueue}
                        AddToErroredSaves={AddToErroredSaves}
                    />
                    <button className={"custom-delete-button"} onClick={() => removeComponent(comp.id)}>✕</button>
                </div>
            );
        }

        if (comp.type === "table-title") {
            const titleVal = _values[comp.table_id]?.[comp.title_id] ?? _values[comp.title_id] ?? "";
            return (
                <div className={"custom-component-table-title"} key={comp.id} >
                    <ValuerAmendFreeTextInput
                        sectionName={comp.table_id}
                        identifier={comp.title_id}
                        textLimit={1024}
                        value={titleVal}
                        field="Table Title"
                        placeholder="Table Title"
                        summaryId={_summaryId}
                        UpdateParentValue={(id, v) => commitValue(comp.table_id, id, v)}
                        AddToQueue={AddToQueue}
                        RemoveFromQueue={RemoveFromQueue}
                        AddToErroredSaves={AddToErroredSaves}
                    />
                </div>
            );
        }

        if (comp.type === "table") {
            const raw = _values[comp.identifier] ?? {};
            const cleaned = Object.fromEntries(Object.entries(raw).filter(([k]) => !k.startsWith(`${_identifier}-title-`)));
            const isFresh = !!cleaned.__fresh;
            delete cleaned.__fresh;

            const allColumnIds = new Set();
            Object.values(cleaned).forEach(row => {
                if (typeof row !== "object") return;
                Object.keys(row).forEach(key => {
                    if (key.startsWith("col-")) allColumnIds.add(key);
                });
            });

            const columns = [...allColumnIds]
                .sort((a, b) => parseInt(a.slice(4)) - parseInt(b.slice(4))) // natural sort
                .map(id => ({ identifier: id, default: "" }));

            return (
                <div className={"custom-component-table"} key={comp.id}>
                    <ValuerAmendExpandableTableContainer
                        summaryId={_summaryId}
                        sectionName={_sectionName}
                        identifier={comp.identifier}
                        columns={columns}
                        isFreshInsert={isFresh} // use the captured flag
                        values={cleaned}
                        titleValue={_values[comp.title_id] ?? ""}
                        UpdateParentValue={UpdateParentValue}
                        AddToQueue={() => {}}
                        RemoveFromQueue={() => {}}
                        AddToErroredSaves={() => {}}
                        setErrorMessage={() => {}}
                        setErrorTitle={() => {}}
                        triggerInitialTitleInsert={() => {
                            const idxMatch = comp.identifier.match(/-table-(\d+)$/);
                            if (!idxMatch) return;
                            const idx = idxMatch[1];
                            const titleKey = `${_identifier}-title-${idx}`;
                            const top = _values[titleKey];
                            const nested = _values[comp.identifier]?.[titleKey];
                            const present = (s) => typeof s === "string" && s.trim() !== "";
                            const alreadyEdited = editedTitleKeysRef.current.has(titleKey);
                            if (!present(top) && !present(nested) && !alreadyEdited) {
                                commitValue(comp.identifier, titleKey, "");
                            }
                        }}
                    />
                    <button onClick={() => removeComponent(comp.id)}>✕</button>
                </div>
            );
        }
        return null;
    };

    /** ------------------------------------------------------------------
     *  JSX
     * ------------------------------------------------------------------*/
    return (
        <div className={"all-custom-components"}>
            <div>{components.map(renderComponent)}</div>

            <div className="add-comp-buttons" >
                <button onClick={addTextbox}><img src={plus} alt={"Add textbox"}/>Add textbox</button>
                <button onClick={addTable}><img src={plus} alt={"Add textbox"}/>Add table</button>
            </div>
        </div>
    );
};

export default ValuerAmendCustomAdditionComponent;
